
import React, { useState, useEffect } from 'react';
import { AlertTriangle, Info, ShieldAlert, RefreshCw, Trash2, Key, Save, Eye, EyeOff, CheckCircle, Brain, GitCommit } from 'lucide-react';
import { Modal, APP_VERSION, CHANGELOG } from './Common';

const CUSTOM_API_KEY_STORAGE = 'math_quiz_custom_api_key';

interface SettingsProps {
    onResetData: () => void;
    onClearQuestions: () => void;
    retentionRate: number;
    setRetentionRate: (rate: number) => void;
}

export const SettingsView = ({ onResetData, onClearQuestions, retentionRate, setRetentionRate }: SettingsProps) => {
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);
    
    // API Key State
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);
    const [isKeyStored, setIsKeyStored] = useState(false);

    useEffect(() => {
        const storedKey = localStorage.getItem(CUSTOM_API_KEY_STORAGE);
        if (storedKey) {
            setApiKeyInput(storedKey);
            setIsKeyStored(true);
        }
    }, []);

    const handleSaveKey = () => {
        if (apiKeyInput.trim()) {
            localStorage.setItem(CUSTOM_API_KEY_STORAGE, apiKeyInput.trim());
            setIsKeyStored(true);
            alert('API Key 已儲存');
        } else {
            localStorage.removeItem(CUSTOM_API_KEY_STORAGE);
            setIsKeyStored(false);
            alert('API Key 已清除，將使用系統預設值');
        }
    };

    const handleConfirmReset = () => {
        onResetData();
        setIsResetModalOpen(false);
    };

    const handleConfirmClear = () => {
        onClearQuestions();
        setIsClearModalOpen(false);
    };

    return (
        <div className="flex-1 bg-gray-100 p-4 md:p-8 overflow-y-auto">
            <Modal
                isOpen={isResetModalOpen}
                title="重置所有資料"
                onCancel={() => setIsResetModalOpen(false)}
                onConfirm={handleConfirmReset}
                isDanger={true}
                confirmText="確認重置"
            >
                <div className="flex items-start text-gray-600">
                    <AlertTriangle className="w-6 h-6 text-red-500 mr-3 shrink-0" />
                    <div>
                        <p className="font-bold text-gray-800 mb-2">此動作將無法復原！</p>
                        <p>這將刪除您所有新增的題目、資料夾、歷史成績與設定，並將系統還原至預設的範例題庫。</p>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isClearModalOpen}
                title="清空所有題目"
                onCancel={() => setIsClearModalOpen(false)}
                onConfirm={handleConfirmClear}
                isDanger={true}
                confirmText="確認清空"
            >
                <div className="flex items-start text-gray-600">
                    <AlertTriangle className="w-6 h-6 text-red-500 mr-3 shrink-0" />
                    <div>
                        <p className="font-bold text-gray-800 mb-2">警告：此動作將刪除題庫中「所有」題目！</p>
                        <p>執行後題庫將變為空白，保留資料夾結構。您需要重新手動新增或匯入題目。此動作無法復原。</p>
                    </div>
                </div>
            </Modal>

            <div className="max-w-4xl mx-auto space-y-6">
                 {/* System Info */}
                 <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center">
                        <Info className="w-5 h-5 text-fudan-blue mr-2" />
                        <h2 className="font-bold text-gray-800">系統資訊</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-gray-500 mb-1">應用程式名稱</label>
                                <div className="font-medium text-gray-900">全方位複習系統</div>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-500 mb-1">目前版本</label>
                                <div className="font-medium text-gray-900 inline-flex items-center">
                                    <span className="bg-blue-100 text-fudan-blue px-2 py-0.5 rounded text-sm font-bold">v{APP_VERSION}</span>
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm text-gray-500 mb-2 flex items-center">
                                    <GitCommit className="w-4 h-4 mr-1" />
                                    版本控制與更新日誌
                                </label>
                                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 max-h-60 overflow-y-auto">
                                    <div className="relative border-l-2 border-gray-200 ml-3 space-y-6">
                                        {CHANGELOG.map((log, idx) => (
                                            <div key={idx} className="ml-6 relative">
                                                <span className={`absolute -left-[31px] top-0 w-4 h-4 rounded-full border-2 border-white ${idx === 0 ? 'bg-fudan-blue' : 'bg-gray-300'}`}></span>
                                                <div className="flex items-baseline justify-between mb-1">
                                                    <h4 className={`text-sm font-bold ${idx === 0 ? 'text-fudan-blue' : 'text-gray-700'}`}>
                                                        v{log.version} - {log.title}
                                                    </h4>
                                                    <span className="text-xs text-gray-400 font-mono">{log.date}</span>
                                                </div>
                                                <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                                                    {log.details.map((detail, i) => (
                                                        <li key={i}>{detail}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                 </div>

                 {/* Learning Algorithm Settings */}
                 <div className="bg-white rounded-xl shadow-sm border border-green-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-green-100 bg-green-50 flex items-center">
                        <Brain className="w-5 h-5 text-green-700 mr-2" />
                        <h2 className="font-bold text-green-800">智慧學習設定 (FSRS)</h2>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="block text-sm font-bold text-gray-700 mb-2 flex justify-between">
                                <span>期望記憶保留率 (Request Retention)</span>
                                <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded text-xs">{(retentionRate * 100).toFixed(0)}%</span>
                            </label>
                            <input 
                                type="range" 
                                min="0.70" 
                                max="0.99" 
                                step="0.01" 
                                value={retentionRate}
                                onChange={(e) => setRetentionRate(parseFloat(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                設定您希望在下次複習時還記得該題目的機率。
                                <br/>• 較高的值 (如 95%)：複習頻率非常高，記憶更牢固，但每日負擔重。
                                <br/>• 較低的值 (如 80%)：複習頻率較低，負擔輕，但可能會忘記更多細節。
                                <br/>• 建議值：<b>90%</b>。
                            </p>
                        </div>
                    </div>
                 </div>

                 {/* API Key Settings */}
                 <div className="bg-white rounded-xl shadow-sm border border-blue-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-blue-100 bg-blue-50 flex items-center">
                        <Key className="w-5 h-5 text-fudan-blue mr-2" />
                        <h2 className="font-bold text-fudan-blue">AI API 設定</h2>
                    </div>
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Google Gemini API Key</label>
                            <p className="text-sm text-gray-500 mb-3">
                                用於題目圖片辨識與詳解自動生成。若未設定，將嘗試使用系統預設的金鑰。
                                您可以前往 <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google AI Studio</a> 獲取金鑰。
                            </p>
                            <div className="relative flex items-center">
                                <input
                                    type={showApiKey ? "text" : "password"}
                                    value={apiKeyInput}
                                    onChange={(e) => setApiKeyInput(e.target.value)}
                                    placeholder="請輸入您的 API Key (AIza...)"
                                    className="w-full pl-4 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                                />
                                <button
                                    onClick={() => setShowApiKey(!showApiKey)}
                                    className="absolute right-3 text-gray-400 hover:text-gray-600"
                                >
                                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center justify-between mt-4">
                             <div className="flex items-center text-sm">
                                 狀態：
                                 {isKeyStored ? (
                                     <span className="text-green-600 font-bold flex items-center ml-2">
                                         <CheckCircle className="w-4 h-4 mr-1" /> 使用自定義金鑰
                                     </span>
                                 ) : (
                                     <span className="text-gray-500 font-bold ml-2">使用系統預設</span>
                                 )}
                             </div>
                             <button
                                onClick={handleSaveKey}
                                className="flex items-center px-4 py-2 bg-fudan-blue hover:bg-blue-800 text-white rounded-lg font-bold shadow-md transition-colors"
                             >
                                <Save className="w-4 h-4 mr-2" />
                                儲存設定
                             </button>
                        </div>
                    </div>
                 </div>

                 {/* Danger Zone */}
                 <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-red-100 bg-red-50 flex items-center">
                        <ShieldAlert className="w-5 h-5 text-red-600 mr-2" />
                        <h2 className="font-bold text-red-800">危險操作區</h2>
                    </div>
                    <div className="p-6 space-y-6">
                        <div>
                            <h3 className="font-bold text-gray-700 mb-2">重置系統</h3>
                            <p className="text-gray-600 mb-4 text-sm">
                                如果您遇到系統異常，或希望清除所有自訂資料，可以使用此功能將系統還原至初始狀態(包含預設範例題)。
                            </p>
                            <button 
                                onClick={() => setIsResetModalOpen(true)}
                                className="flex items-center px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-bold transition-colors shadow-sm"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                重置系統至預設值
                            </button>
                        </div>

                        <hr className="border-red-100"/>

                        <div>
                            <h3 className="font-bold text-gray-700 mb-2">清空題庫</h3>
                            <p className="text-gray-600 mb-4 text-sm">
                                如果您想清空目前所有題目，以便匯入全新的題庫資料，請使用此功能。此操作只會刪除題目，保留資料夾結構。
                            </p>
                            <button 
                                onClick={() => setIsClearModalOpen(true)}
                                className="flex items-center px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-bold transition-colors shadow-sm"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                清空所有題目
                            </button>
                        </div>
                    </div>
                 </div>
            </div>
        </div>
    );
};
